import React from 'react'
import { InferType } from 'yup'
import { captureException } from '@sentry/react'
import { useMutation, useQuery } from '@tanstack/react-query'
import { useParams } from 'react-router-dom'
import { Modal } from '../../../common/Modal'
import { ModalHeader } from '../../../common/Modal/ModalHeader'
import { ModalBody } from '../../../common/Modal/ModalBody'
import { useAppTranslation } from '../../../../hooks/useAppTranslation'
import { ToastType } from '../../../common/Toast/toast-type'
import { useToastNotifications } from '../../../../hooks/useToastNotification'
import { useCurrentSociety } from '../../../../hooks/useCurrentSociety'
import { NBinder } from '../../../../interfaces/services/binder.interfaces'
import {
  binderQueries,
  createBinderDocument,
  patchBinderDocument,
} from '../../../../api/binder'
import { societyDocumentFolderCreateSchema } from '../../../../forms/schemas/society_document_folder_create'
import { CreateUpdateBinderFolderForm } from '../CreateUpdateBinderFolderForm'

// Utility to extract id.ext from a URL or return as-is if already in that format
function extractThumbnailFilename(thumbnail?: string): string | undefined {
  if (!thumbnail) return undefined
  try {
    const { pathname } = new URL(thumbnail)
    const filename = pathname.substring(pathname.lastIndexOf('/') + 1)
    return filename
  } catch {
    return thumbnail
  }
}

interface CreateUpdateBinderModalProps {
  show: boolean
  binderId: string
  close: () => void
  parentId?: string
  children?: React.ReactNode
  id?: string
  published?: boolean
}

export const CreateUpdateBinderModal = ({
  id,
  binderId,
  parentId,
  children,
  show,
  close,
  published,
}: CreateUpdateBinderModalProps): React.ReactNode => {
  const { id: societyId, unitId } = useParams()
  const { translate } = useAppTranslation()
  const { society } = useCurrentSociety()
  const { data: folder, isLoading: isLoadingFolder } = useQuery(
    binderQueries.getDocument(society?._id, id)
  )
  const { setToastNotification } = useToastNotifications()

  const updateMode = !!id

  const showErrorMessage = (): void => {
    const message = updateMode
      ? 'createUpdateFolder.flashMessage.updateFolderFailure'
      : 'createUpdateFolder.flashMessage.createFolderFailure'
    setToastNotification(ToastType.DANGER, translate(message))
  }

  const showSuccessMessage = (): void => {
    const message = updateMode
      ? 'createUpdateFolder.flashMessage.updateFolderSuccess'
      : 'createUpdateFolder.flashMessage.createFolderSuccess'
    setToastNotification(ToastType.SUCCESS, translate(message))
  }

  const { mutate: mutatePatchDocument, isPending: isPatchingDocument } =
    useMutation({
      mutationFn: async (params: {
        id: string
        body: NBinder.NPatch.IRequestBody
      }) => {
        return patchBinderDocument({ id: params.id, body: params.body })
      },
      onSuccess: () => {
        close()
        showSuccessMessage()
      },
      onError: () => {
        showErrorMessage()
      },
    })

  const { mutate: mutateCreateFolder, isPending: isCreatingFolder } =
    useMutation({
      mutationFn: async (params: {
        name: string
        binderId: string
        description?: string
        thumbnail?: string
        parentId?: string
        notificationSettings?: NBinder.NCreate.IRequestBody['notificationSettings']
      }) => {
        const body: NBinder.NCreate.IRequestBody = {
          ...{
            name: params.name,
            binderId: params.binderId,
            description: params.description,
            thumbnail: params.thumbnail,
            filename: params.name,
            type: 'folder',
            parentId: params.parentId,
            societyId,
            unitId,
            notificationSettings: params.notificationSettings,
          },
        }
        return createBinderDocument(societyId!, body)
      },
      onSuccess: () => {
        close()
        showSuccessMessage()
      },
      onError: () => {
        showErrorMessage()
      },
    })

  const onSubmit = async (
    data: InferType<typeof societyDocumentFolderCreateSchema>
  ): Promise<void> => {
    const cleanedData = {
      ...data,
      thumbnail: extractThumbnailFilename(data.thumbnail),
    }
    if (updateMode) {
      mutatePatchDocument({ id, body: cleanedData })
    } else {
      mutateCreateFolder({
        name: cleanedData.name,
        description: cleanedData.description,
        thumbnail: cleanedData.thumbnail,
        notificationSettings: cleanedData.notificationSettings,
        parentId,
        binderId,
      })
    }
  }

  const onError = (error: unknown): void => {
    captureException(error)
    showErrorMessage()
  }

  const loading = isCreatingFolder || isPatchingDocument

  if (isLoadingFolder || society === undefined) {
    return null
  }

  const notificationSettingsOptions: {
    value: 'none' | 'notification'
    label: string
  }[] = [
    {
      value: 'none',
      label: translate('common.notificationSettings.none'),
    },
    {
      value: 'notification',
      label: translate('common.notificationSettings.notification'),
    },
  ]

  return (
    <>
      {children}
      <Modal show={show}>
        {{
          header: (
            <ModalHeader onClose={close}>
              {translate(
                updateMode
                  ? 'createUpdateFolder.editFolderTitle'
                  : 'createUpdateFolder.addNewFolderTitle'
              )}
            </ModalHeader>
          ),
          body: (
            <ModalBody>
              <CreateUpdateBinderFolderForm
                societyId={society?._id}
                onError={onError}
                onSubmit={onSubmit}
                onClose={close}
                loading={loading}
                folder={folder}
                optionSettings={notificationSettingsOptions}
                published={published}
              />
            </ModalBody>
          ),
        }}
      </Modal>
    </>
  )
}
